import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, Clock, Check, ArrowRight, Loader2, CalendarCheck } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Reveal, EASE } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM",
];

const inputCls =
  "w-full bg-transparent border-b border-white/15 py-3 text-paper placeholder:text-paper/30 focus:outline-none focus:border-acid transition-colors font-sans text-lg";
const labelCls = "font-mono text-[11px] uppercase tracking-[0.2em] text-paper/45 mb-1 block";

export const DemoSection = () => {
  const [form, setForm] = useState({ name: "", email: "", use_case: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!validEmail(form.email)) return toast.error("Please enter a valid email");
    if (!date) return toast.error("Please pick a preferred date");
    if (!time) return toast.error("Please pick a preferred time");
    if (!form.use_case.trim()) return toast.error("Tell us your use case");

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        name: form.name.trim(),
        email: form.email.trim(),
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        use_case: form.use_case.trim(),
      });
      setConfirmation({ ...data, dateObj: date });
      toast.success("Demo booked — check the summary below.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setConfirmation(null);
    setForm({ name: "", email: "", use_case: "" });
    setDate(null);
    setTime("");
  };

  return (
    <section id="book" data-testid="book-section" className="py-28 md:py-40 border-t border-white/10 spotlight">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Left intro */}
          <div className="lg:col-span-4">
            <Reveal>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-acid">
                / Book a demo
              </span>
              <h2 className="mt-4 font-display font-bold text-4xl md:text-6xl tracking-tight leading-[0.95] text-paper">
                See it on <span className="font-serif-i italic font-normal">your</span> pipeline.
              </h2>
              <p className="mt-6 text-paper/55 leading-relaxed max-w-sm">
                A 30-minute walkthrough with a product specialist. No slides —
                we plug into a sample of your data and show the forecast live.
              </p>
              <div className="mt-10 space-y-3 font-mono text-xs text-paper/50">
                {["30 minutes, tailored", "No credit card", "Live forecast on real data"].map((t) => (
                  <div key={t} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-acid" /> {t}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right: form OR confirmation */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              {!confirmation ? (
                <motion.form
                  key="form"
                  data-testid="demo-form"
                  onSubmit={submit}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="rounded-[4px] border border-white/10 bg-ink-2/60 backdrop-blur p-6 md:p-10"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className={labelCls} htmlFor="name">Full name</label>
                      <input
                        id="name"
                        data-testid="input-name"
                        className={inputCls}
                        placeholder="Jordan Rivera"
                        value={form.name}
                        onChange={update("name")}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="email">Work email</label>
                      <input
                        id="email"
                        data-testid="input-email"
                        type="email"
                        className={inputCls}
                        placeholder="jordan@company.com"
                        value={form.email}
                        onChange={update("email")}
                      />
                    </div>

                    {/* Date */}
                    <div>
                      <label className={labelCls}>Preferred date</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="date-trigger"
                            className={`${inputCls} flex items-center justify-between text-left ${!date && "text-paper/30"}`}
                          >
                            {date ? format(date, "EEE, MMM d, yyyy") : "Select a date"}
                            <CalendarIcon className="w-4 h-4 text-acid" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-ink-2 border-white/15" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Time */}
                    <div>
                      <label className={labelCls}>Preferred time</label>
                      <Select value={time} onValueChange={setTime}>
                        <SelectTrigger
                          data-testid="time-trigger"
                          className="w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 h-auto text-lg text-paper focus:ring-0 focus:border-acid data-[placeholder]:text-paper/30"
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-acid" />
                            <SelectValue placeholder="Select a time" />
                          </div>
                        </SelectTrigger>
                        <SelectContent className="bg-ink-2 border-white/15 text-paper">
                          {TIME_SLOTS.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`time-${t}`} className="focus:bg-acid/15 focus:text-paper">
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-8">
                    <label className={labelCls} htmlFor="usecase">What do you want to solve?</label>
                    <textarea
                      id="usecase"
                      data-testid="input-usecase"
                      rows={3}
                      className={`${inputCls} resize-none`}
                      placeholder="We're missing forecast on 40+ deals a quarter and want to..."
                      value={form.use_case}
                      onChange={update("use_case")}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="submit-booking"
                    disabled={loading}
                    className="group mt-10 relative overflow-hidden rounded-full bg-acid px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink font-bold flex items-center gap-2 disabled:opacity-60"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</>
                    ) : (
                      <>Confirm my demo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
                    )}
                  </button>
                </motion.form>
              ) : (
                <ConfirmationCard key="confirm" data={confirmation} onReset={reset} />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

function ConfirmationCard({ data, onReset }) {
  const d = data.dateObj;
  return (
    <motion.div
      data-testid="confirmation-summary"
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, ease: EASE }}
      className="rounded-[4px] border border-acid/30 bg-ink-2/70 backdrop-blur overflow-hidden"
    >
      <div className="flex items-center gap-3 px-6 md:px-8 py-5 border-b border-white/10 bg-acid/[0.06]">
        <div className="w-9 h-9 rounded-full bg-acid flex items-center justify-center">
          <CalendarCheck className="w-5 h-5 text-ink" />
        </div>
        <div>
          <p className="font-display font-bold text-paper text-lg leading-none">You're booked in.</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 mt-1">
            Confirmation {data.confirmation_code}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Calendar-style visual */}
        <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-white/10 flex flex-col items-center">
          <div className="rounded-[3px] border border-white/10 overflow-hidden">
            {/* fake header strip like a torn calendar */}
            <div className="bg-acid text-ink text-center py-2 font-mono text-[11px] uppercase tracking-[0.2em] font-bold">
              {format(d, "MMMM yyyy")}
            </div>
            <Calendar
              mode="single"
              selected={d}
              month={d}
              disabled
              data-testid="confirmation-calendar"
              className="[&_.rdp-nav]:hidden [&_.rdp-caption_label]:hidden"
            />
          </div>
          <p className="mt-4 font-display font-extrabold text-4xl text-paper">
            {format(d, "EEEE")}
          </p>
          <p className="font-serif-i italic text-acid text-2xl -mt-1">{format(d, "MMM d")}</p>
        </div>

        {/* Details */}
        <div className="p-6 md:p-8 space-y-5">
          <SummaryRow label="Name" value={data.name} testid="summary-name" />
          <SummaryRow label="Email" value={data.email} testid="summary-email" />
          <SummaryRow label="Date" value={format(d, "EEE, MMM d, yyyy")} testid="summary-date" />
          <SummaryRow label="Time" value={data.preferred_time} testid="summary-time" />
          <SummaryRow label="Use case" value={data.use_case} testid="summary-usecase" />

          <div className="pt-4">
            <p className="text-paper/50 text-sm leading-relaxed">
              A calendar invite is on its way to <span className="text-paper">{data.email}</span>.
              We can't wait to show you what AXIOM sees.
            </p>
            <button
              onClick={onReset}
              data-testid="book-another"
              className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-paper/60 hover:text-acid border-b border-white/20 hover:border-acid pb-1 transition-colors"
            >
              Book another demo
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function SummaryRow({ label, value, testid }) {
  return (
    <div className="flex flex-col">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/40">{label}</span>
      <span data-testid={testid} className="text-paper mt-0.5 break-words">{value}</span>
    </div>
  );
}
